import React, { useState } from 'react';
import { Box, Typography, Button, useTheme, useMediaQuery, IconButton, Menu, MenuItem, SxProps, Theme } from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import WaveScoopLogoWhite from '../../assets/images/WaveScoopLogoWhite.jsx';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from '../../contexts/AuthContext';
import { usePrelaunch } from '../../contexts/PrelaunchContext';

interface PublicHeaderProps {
  sx?: SxProps<Theme>;
}

const PublicHeader: React.FC<PublicHeaderProps> = ({ sx: externalSx }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated } = useAuth();
  const { isPrelaunch } = usePrelaunch();

  // Navigation links
  const navLinks = [
    { path: '/features', label: 'Features' },
    { path: '/pricing', label: 'Pricing' },
    { path: '/blog', label: 'News' },
    { path: '/support', label: 'Contact' }
  ];

  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isMobileMenuOpen = Boolean(mobileMenuAnchorEl);

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchorEl(null);
  };

  const handleFeaturesClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (currentPath === '/') {
      // Already on landing page, just scroll
      document.querySelector('#features')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Navigate to landing page, then scroll
      navigate('/');
      setTimeout(() => {
        document.querySelector('#features')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (currentPath === '/') {
      // Already on landing page, scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Navigate to landing page
      navigate('/');
    }
  };

  return (
    <Box 
      component="header"
      sx={{
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 1.5, md: 2.5 },
        width: '100%',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        backgroundColor: 'transparent',
        backdropFilter: 'blur(10px) saturate(100%)',
        WebkitBackdropFilter: 'blur(10px) saturate(110%)',
        borderBottom: 'none',
        transition: 'all 0.3s ease',
        ...externalSx
      }}
    >
      {/* Centered container - matching hero width */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'auto 1fr', md: 'auto 1fr auto' },
            alignItems: 'center',
            width: '100%',
            maxWidth: { xs: '100%', sm: '600px', md: '720px' },
            gap: { xs: 1.5, md: 2.5 }
          }}
        >
        {/* Logo */}
        <Box
          component="a"
          href="/"
          onClick={handleLogoClick}
          sx={{
            color: 'white',
            textDecoration: 'none',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            fontFamily: 'Sora',
            cursor: 'pointer',
          }}
        >
          <Box
            sx={{
              width: { xs: 28, md: 32 },
              height: { xs: 28, md: 32 },
              marginRight: 1.2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <WaveScoopLogoWhite 
              style={{ 
                width: '100%', 
                height: '100%',
                fill: 'white',
              }} 
            />
          </Box>
          <Typography component="span" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', md: '0.9rem' } }}>
            WaveScoop
          </Typography>
        </Box>
      
        {/* Navigation Links - Desktop - Dead Center */}
        <Box 
          component="nav" 
          sx={{ 
            display: { xs: 'none', md: 'flex' }, 
            alignItems: 'center', 
            gap: 2.5,
            justifySelf: 'center'
          }}
        >
        {navLinks.map(link => (
          link.path === '/features' ? (
            <Typography 
              key={link.path}
              component="a"
              href="#features"
              onClick={handleFeaturesClick}
              sx={{ 
                color: 'white', 
                textDecoration: 'none',
                cursor: 'pointer',
                fontSize: { xs: '0.75rem', md: '0.8rem' },
                position: 'relative',
                '&::after': currentPath === '/' ? {
                  content: '""',
                  position: 'absolute',
                  bottom: -5,
                  left: 0,
                  width: '100%',
                  height: 2,
                  backgroundColor: 'white'
                } : {}
              }}
            >
              {link.label}
            </Typography>
          ) : (
            <Typography 
              key={link.path}
              component={Link} 
              to={link.path} 
              sx={{ 
                color: 'white', 
                textDecoration: 'none',
                fontSize: { xs: '0.75rem', md: '0.8rem' },
                position: 'relative',
                '&::after': currentPath === link.path ? {
                  content: '""',
                  position: 'absolute',
                  bottom: -5,
                  left: 0,
                  width: '100%',
                  height: 2,
                  backgroundColor: 'white'
                } : {}
              }}
            >
              {link.label}
            </Typography>
          )
        ))}
        </Box>

        {/* Login/Dashboard Button - Right - Desktop Only */}
        {!isPrelaunch && !isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, justifySelf: 'end' }}>
            <Button 
              component={Link} 
              to={isAuthenticated ? "/dashboard" : "/login"} 
              variant="contained" 
              sx={{ 
                bgcolor: theme.palette.primary.dark, 
                color: theme.palette.getContrastText(theme.palette.primary.dark),
                borderRadius: '28px',
                px: 2.75,
                py: 0.7,
                fontSize: { xs: '0.75rem', md: '0.8rem' },
                '&:hover': {
                  bgcolor: theme.palette.primary.main
                }
              }}
            >
              {isAuthenticated ? "Dashboard" : "Login"}
            </Button>

            {!isAuthenticated && (
              <Button
                component={Link}
                to="/login?source=try-now"
                variant="contained"
                sx={{
                  bgcolor: 'white',
                  color: '#0a2a4d',
                  borderRadius: '28px',
                  px: 2.8,
                  py: 0.7,
                  fontSize: { xs: '0.75rem', md: '0.8rem' },
                  boxShadow: '0 8px 20px rgba(10, 42, 77, 0.12)',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.92)'
                  }
                }}
              >
                Try WaveScoop Now
              </Button>
            )}
          </Box>
        )}

        {/* Mobile Menu Button */}
        {!isPrelaunch && (
          <Box sx={{ display: { xs: 'flex', md: 'none' }, justifySelf: 'end' }}>
          <IconButton
            size="large"
            edge="end"
            color="inherit"
            aria-label="menu"
            onClick={handleMobileMenuOpen}
          >
            <MenuIcon />
          </IconButton>
          </Box>
        )}
      </Box>

      {/* Mobile Menu */}
      {!isPrelaunch && (
        <Menu
          anchorEl={mobileMenuAnchorEl}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          id="mobile-public-menu"
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={isMobileMenuOpen}
          onClose={handleMobileMenuClose}
          sx={{ display: { xs: 'block', md: 'none' } }}
        >
          {!isPrelaunch && navLinks.map(link => (
            link.path === '/features' ? (
              <MenuItem 
                key={link.path}
                onClick={(e) => {
                  handleMobileMenuClose();
                  handleFeaturesClick(e);
                }}
                selected={currentPath === '/'}
              >
                {link.label}
              </MenuItem>
            ) : (
              <MenuItem 
                key={link.path} 
                component={Link} 
                to={link.path} 
                onClick={handleMobileMenuClose}
                selected={currentPath === link.path}
              >
                {link.label}
              </MenuItem>
            )
          ))}
          {!isPrelaunch && (
            <MenuItem component={Link} to={isAuthenticated ? "/dashboard" : "/login"} onClick={handleMobileMenuClose}>
              {isAuthenticated ? "Dashboard" : "Login"}
            </MenuItem>
          )}
          {!isPrelaunch && !isAuthenticated && (
            <MenuItem component={Link} to="/login?source=try-now" onClick={handleMobileMenuClose}>
              Try WaveScoop Now
            </MenuItem>
          )}
        </Menu>
      )}
    </Box>
  );
};

export default PublicHeader; 
